import { cn } from "@/lib/utils";
import { decisionInfo, decisionV2, EVIDENCE_TIER } from "@/lib/format";
import { Loader2, BadgeCheck } from "lucide-react";

export function Spinner({ className }) {
  return (
    <div className="flex items-center justify-center py-16" data-testid="loading-spinner">
      <Loader2 className={cn("h-6 w-6 animate-spin text-editorial/60", className)} />
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, testid, eyebrow }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-7" data-testid={testid}>
      <div>
        {eyebrow && <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-editorial">{eyebrow}</p>}
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-ink">{title}</h1>
        {subtitle && <p className="mt-2 text-sm text-slate-500 max-w-2xl leading-relaxed">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function KpiCard({ label, value, sub, accent = "navy", testid }) {
  const accentMap = {
    navy: "text-ink",
    positive: "text-[#1E9E6A]",
    negative: "text-coral",
    warning: "text-[#8a6d12]",
    muted: "text-slate-500",
  };
  return (
    <div
      className="rounded-xl border border-ink/10 bg-white p-5 shadow-sm transition-all duration-200 hover:border-editorial/30 hover:shadow-md"
      data-testid={testid}
    >
      <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">{label}</p>
      <p className={cn("kpi-number mt-2.5 text-[28px] leading-none font-semibold", accentMap[accent])}>{value}</p>
      {sub && <p className="mt-2 text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

export function DecisionBadge({ status, className }) {
  const info = decisionInfo(status);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
        info.cls,
        className
      )}
      data-testid={`decision-badge-${status}`}
    >
      {info.label}
    </span>
  );
}

export function DecisionV2Badge({ status, className }) {
  const info = decisionV2(status);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
        info.cls, className
      )}
      title={info.note}
      data-testid={`decision-v2-${status}`}
    >
      {info.label}
    </span>
  );
}

export function EvidenceBadge({ tier, className, showNote = false }) {
  const info = EVIDENCE_TIER[tier] || EVIDENCE_TIER.E;
  return (
    <span
      className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold whitespace-nowrap", info.cls, className)}
      title={info.note}
      data-testid={`evidence-tier-${tier}`}
    >
      {info.label}{showNote && <span className="font-normal opacity-70">· {info.note}</span>}
    </span>
  );
}

// "Value, Evidence-Graded" trust seal — Warm Gold
export function VerifiedSeal({ className, label = "Value, Evidence-Graded" }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full border border-gold/50 bg-gold/15 px-2.5 py-0.5 text-xs font-semibold text-[#8a6d12]", className)} data-testid="verified-seal">
      <BadgeCheck className="h-3.5 w-3.5" /> {label}
    </span>
  );
}

// Signature brand device: the AI Value Label
export function AIValueLabel({ title = "AI Value Label", fields = [], status, nextAction, verified, className, testid = "ai-value-label" }) {
  const toneMap = {
    positive: "text-[#1E9E6A]", negative: "text-coral", ink: "text-ink", editorial: "text-editorial", muted: "text-slate-500",
  };
  return (
    <div className={cn("relative overflow-hidden rounded-xl border border-ink/15 bg-white shadow-sm", className)} data-testid={testid}>
      <div className="h-1 w-full bg-gold" />
      <div className="flex items-center justify-between px-5 pt-4">
        <p className="font-heading text-sm font-semibold uppercase tracking-[0.14em] text-ink">{title}</p>
        {verified && <VerifiedSeal />}
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 px-5 py-4 sm:grid-cols-3">
        {fields.map((f) => (
          <div key={f.label} data-testid={`avl-field-${f.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{f.label}</p>
            <p className={cn("kpi-number mt-1 text-xl font-semibold", toneMap[f.tone] || "text-ink")}>{f.value}</p>
          </div>
        ))}
      </div>
      {(status || nextAction) && (
        <div className="flex flex-wrap items-center gap-3 border-t border-ink/10 px-5 py-3">
          {status && <DecisionV2Badge status={status} />}
          {nextAction && <span className="text-xs text-slate-500"><span className="font-semibold text-ink">Next action:</span> {nextAction}</span>}
        </div>
      )}
    </div>
  );
}

export function EmptyState({ title, body, children, icon: Icon, testid }) {
  return (
    <div
      className="rounded-xl border border-dashed border-ink/20 bg-white p-12 text-center"
      data-testid={testid || "empty-state"}
    >
      {Icon && (
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-editorial/10">
          <Icon className="h-6 w-6 text-editorial" />
        </div>
      )}
      <h3 className="font-heading text-xl font-semibold text-ink">{title}</h3>
      {body && <p className="mx-auto mt-2 max-w-md text-sm text-slate-500 leading-relaxed">{body}</p>}
      {children && <div className="mt-6 flex flex-wrap items-center justify-center gap-3">{children}</div>}
    </div>
  );
}

export function Card({ title, subtitle, actions, children, className, testid }) {
  return (
    <div className={cn("rounded-xl border border-ink/10 bg-white shadow-sm", className)} data-testid={testid}>
      {(title || actions) && (
        <div className="flex items-center justify-between border-b border-ink/10 px-5 py-4">
          <div>
            {title && <h3 className="text-sm font-semibold text-ink">{title}</h3>}
            {subtitle && <p className="mt-0.5 text-xs text-slate-400">{subtitle}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}
